// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import MobileStepper from '@mui/material/MobileStepper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
    formatFeatureAnnouncementDate,
    resolveFeatureAnnouncementCtaUrl,
} from 'pages/newFeatures/featureAnnouncements';

const renderAnnouncementHtml = (content, organisationId) => {
    const normalizedContent = organisationId
        ? (content || '').replaceAll(':organisationId', organisationId)
        : (content || '');
    return DOMPurify.sanitize(marked(normalizedContent));
};
const isExternalUrl = (url) => /^https?:\/\//.test(url);

const FeatureAnnouncementsModal = ({ open, announcements = [], organisationId, onClose }) => {
    const [activeStep, setActiveStep] = useState(0);
    const orderedAnnouncements = useMemo(
        () => announcements
            .map((announcement, index) => ({ announcement, index }))
            .sort((left, right) => {
                const leftTime = new Date(left.announcement.publishedAt).getTime();
                const rightTime = new Date(right.announcement.publishedAt).getTime();

                if (leftTime !== rightTime) {
                    return leftTime - rightTime;
                }

                return left.index - right.index;
            })
            .map((entry) => entry.announcement),
        [announcements],
    );
    const totalSteps = orderedAnnouncements.length;

    const announcement = orderedAnnouncements[activeStep];
    const ctaUrl = useMemo(
        () => resolveFeatureAnnouncementCtaUrl(announcement?.ctaUrl, { organisationId }),
        [announcement?.ctaUrl, organisationId],
    );
    const openExternally = announcement?.ctaExternal || isExternalUrl(ctaUrl);

    const handleClose = () => {
        setActiveStep(0);
        onClose?.();
    };

    const handleNext = () => {
        if (activeStep >= totalSteps - 1) {
            handleClose();
            return;
        }

        setActiveStep((step) => step + 1);
    };

    const handleBack = () => {
        setActiveStep((step) => Math.max(step - 1, 0));
    };

    if (!announcement) {
        return null;
    }

    return (
        <Dialog open={ open } onClose={ handleClose } maxWidth="md" fullWidth>
            <DialogTitle>Opdateringer</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={ 2.5 }>
                    <Stack spacing={ 1 }>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 1 } alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Typography variant="h5">
                                { announcement.title }
                            </Typography>
                            <Chip size="small" label={ formatFeatureAnnouncementDate(announcement.publishedAt) } />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            { announcement.summary }
                        </Typography>
                    </Stack>

                    <Box
                        sx={{
                            '& h1, & h2, & h3': { mt: 0 },
                            '& p:last-child': { mb: 0 },
                            '& ul, & ol': { pl: 3 },
                            '& a': { color: 'primary.main' },
                        }}
                        dangerouslySetInnerHTML={{ __html: renderAnnouncementHtml(announcement.content, organisationId) }}
                    />

                    { announcement.ctaLabel && ctaUrl && (
                        openExternally ? (
                            <Button component={ Link } href={ ctaUrl } target="_blank" rel="noreferrer" variant="contained" onClick={ handleClose }>
                                { announcement.ctaLabel }
                            </Button>
                        ) : (
                            <Button component={ RouterLink } to={ ctaUrl } variant="contained" onClick={ handleClose }>
                                { announcement.ctaLabel }
                            </Button>
                        )
                    ) }
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2, px: 3, py: 2 }}>
                <Button onClick={ handleClose } color="inherit">
                    Luk
                </Button>
                <MobileStepper
                    variant="dots"
                    steps={ totalSteps }
                    position="static"
                    activeStep={ activeStep }
                    sx={{ flex: 1, maxWidth: 220, backgroundColor: 'transparent', p: 0 }}
                    nextButton={ (
                        <Button size="small" onClick={ handleNext } variant="contained">
                            { activeStep === totalSteps - 1 ? 'Færdig' : 'Næste' }
                        </Button>
                    ) }
                    backButton={ (
                        <Button size="small" onClick={ handleBack } disabled={ activeStep === 0 }>
                            Tilbage
                        </Button>
                    ) }
                />
            </DialogActions>
        </Dialog>
    );
};

export default FeatureAnnouncementsModal;
